import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from "react";
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Clock, 
  Zap, 
  Shield,
  ChevronDown,
  ArrowRight
} from "lucide-react";

const CaseStudies = () => {
  const [expandedCases, setExpandedCases] = useState<number[]>([]);

  const toggleCase = (index: number) => {
    setExpandedCases(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const caseStudies = [
    {
      title: "DataVision Analytics Platform",
      client: "Fortune 45 Manufacturing Company",
      industry: "Enterprise Analytics",
      challenge: "Scattered data across multiple systems with no unified analytics or real-time insights for decision making.",
      solution: "Comprehensive enterprise analytics platform with real-time dashboards, predictive analytics, and automated reporting.",
      results: [
        { metric: "Decision Speed", value: "75% faster", icon: TrendingUp },
        { metric: "Data Accuracy", value: "99.5% precision", icon: Users },
        { metric: "Cost Reduction", value: "$3.2M saved", icon: Clock }
      ],
      technologies: ["React", "Python", "Apache Spark", "Tableau", "PostgreSQL", "Machine Learning"],
      duration: "16 months",
      teamSize: "22 developers"
    },
    {
      title: "CloudFirst Infrastructure Migration",
      client: "Global Financial Institution",
      industry: "Cloud Infrastructure",
      challenge: "Legacy on-premise infrastructure limiting scalability and increasing operational costs significantly.",
      solution: "Complete cloud migration with microservices architecture, containerization, and automated deployment pipelines.",
      results: [
        { metric: "Infrastructure Cost", value: "60% reduction", icon: TrendingUp },
        { metric: "System Uptime", value: "99.99% availability", icon: Users },
        { metric: "Deployment Speed", value: "90% faster", icon: Clock }
      ],
      technologies: ["AWS", "Docker", "Kubernetes", "Terraform", "Jenkins", "Monitoring Tools"],
      duration: "14 months",
      teamSize: "28 developers"
    },
    {
      title: "IntelliPredict AI Platform",
      client: "Leading Automotive Manufacturer",
      industry: "Artificial Intelligence",
      challenge: "Manual quality control processes resulting in defects and production inefficiencies in manufacturing lines.",
      solution: "AI-powered predictive maintenance and quality control system with computer vision and machine learning algorithms.",
      results: [
        { metric: "Defect Reduction", value: "85% decrease", icon: TrendingUp },
        { metric: "Production Efficiency", value: "40% improvement", icon: Users },
        { metric: "Maintenance Costs", value: "50% savings", icon: Clock }
      ],
      technologies: ["TensorFlow", "OpenCV", "Python", "Edge Computing", "IoT Sensors", "Real-time Analytics"],
      duration: "18 months",
      teamSize: "25 developers"
    },
    {
      title: "CyberShield Security Framework",
      client: "International Healthcare Network",
      industry: "Cybersecurity",
      challenge: "Increasing cyber threats and regulatory compliance requirements for patient data protection across multiple facilities.",
      solution: "Comprehensive cybersecurity framework with threat detection, incident response, and compliance monitoring systems.",
      results: [
        { metric: "Threat Detection", value: "95% accuracy", icon: TrendingUp },
        { metric: "Incident Response", value: "80% faster", icon: Clock },
        { metric: "Compliance Rate", value: "100% achieved", icon: Users }
      ],
      technologies: ["SIEM Tools", "Threat Intelligence", "Zero Trust Architecture", "Blockchain", "Encryption", "Monitoring"],
      duration: "12 months",
      teamSize: "20 developers"
    },
    {
      title: "DevOps Accelerator Pipeline",
      client: "Multi-National Technology Company",
      industry: "DevOps & Automation",
      challenge: "Slow software delivery cycles and manual deployment processes hindering rapid product development and releases.",
      solution: "Complete DevOps transformation with CI/CD pipelines, automated testing, and infrastructure as code implementation.",
      results: [
        { metric: "Deployment Frequency", value: "10x increase", icon: TrendingUp },
        { metric: "Lead Time", value: "70% reduction", icon: Clock },
        { metric: "Failure Rate", value: "90% decrease", icon: Users }
      ],
      technologies: ["GitLab CI/CD", "Ansible", "Terraform", "Prometheus", "Grafana", "Automated Testing"],
      duration: "10 months",
      teamSize: "18 developers"
    },
    {
      title: "MobileFirst Enterprise App Suite",
      client: "Global Retail Corporation",
      industry: "Mobile Development",
      challenge: "Lack of mobile presence affecting customer engagement and limiting business reach in mobile-first markets.",
      solution: "Comprehensive mobile app development with cross-platform solutions, offline capabilities, and seamless integrations.",
      results: [
        { metric: "Mobile Engagement", value: "300% increase", icon: Users },
        { metric: "Customer Acquisition", value: "250% growth", icon: TrendingUp },
        { metric: "Revenue Growth", value: "45% increase", icon: Clock }
      ],
      technologies: ["React Native", "Flutter", "Progressive Web Apps", "Firebase", "API Gateway", "Analytics"],
      duration: "15 months",
      teamSize: "24 developers"
    }
  ];

  return (
    <section id="case-studies" className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 text-sm px-4 py-1.5">
            Proven Results
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-foreground bg-gradient-primary bg-clip-text text-transparent pb-2">
            Client Success Stories
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            How we've helped global enterprises achieve measurable business outcomes through digital transformation
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {caseStudies.map((study, index) => (
            <Card 
              key={index} 
              className="group hover:shadow-elegant transition-all duration-300 hover:-translate-y-1 bg-gradient-card border-0 shadow-card overflow-hidden"
            >
              <CardHeader className="pt-6 md:pt-8 px-6 md:px-8">
                <div className="mb-3">
                  <Badge variant="secondary" className="bg-muted text-foreground text-xs px-3 py-1">
                    {study.industry}
                  </Badge>
                </div>
                <CardTitle className="text-xl md:text-2xl font-bold text-foreground mb-2">
                  {study.title}
                </CardTitle>
                <p className="text-sm md:text-base text-primary font-medium">{study.client}</p>
              </CardHeader>

              <CardContent className="px-6 md:px-8 pb-6 md:pb-8">
                <div className="space-y-6">
                  <div>
                    <h4 className="text-base md:text-lg font-semibold text-foreground mb-2">Challenge</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{study.challenge}</p>
                  </div>

                  <div>
                    <h4 className="text-base md:text-lg font-semibold text-foreground mb-2">Solution</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{study.solution}</p>
                  </div>

                  <div>
                    <h4 className="text-base md:text-lg font-semibold text-foreground mb-3">Key Results</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {study.results.map((result, resultIndex) => (
                        <div key={resultIndex} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                          <div className="bg-gradient-primary p-2 rounded-lg">
                            <result.icon className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <div className="text-body-sm font-semibold text-foreground">{result.value}</div>
                            <div className="text-caption text-muted-foreground">{result.metric}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Technologies Used</h4>
                    <div className="flex flex-wrap gap-2">
                      {study.technologies.map((tech, techIndex) => (
                        <Badge key={techIndex} variant="outline" className="text-xs">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between text-sm text-muted-foreground border-t pt-4">
                    <span>Duration: {study.duration}</span>
                    <span>Team: {study.teamSize}</span>
                  </div>

                  <Collapsible>
                    <CollapsibleTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full group-hover:border-primary transition-all duration-300"
                        onClick={() => toggleCase(index)}
                      >
                        {expandedCases.includes(index) ? "Show Less" : "View Full Case Study"}
                        <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${expandedCases.includes(index) ? 'rotate-180' : ''}`} />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-4 space-y-4">
                      <div>
                        <h5 className="font-semibold text-foreground mb-2">Detailed Challenge</h5>
                        <p className="text-sm text-muted-foreground">{study.challenge}</p>
                      </div>
                      <div>
                        <h5 className="font-semibold text-foreground mb-2">Our Solution</h5>
                        <p className="text-sm text-muted-foreground">{study.solution}</p>
                      </div>
                      <div>
                        <h5 className="font-semibold text-foreground mb-2">Technologies Used</h5>
                        <div className="flex flex-wrap gap-2">
                          {study.technologies.map((tech, techIndex) => (
                            <Badge key={techIndex} variant="secondary" className="text-xs">
                              {tech}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div className="text-center">
                          <Clock className="h-5 w-5 text-primary mx-auto mb-1" />
                          <div className="text-sm font-semibold text-foreground">{study.duration}</div>
                          <div className="text-xs text-muted-foreground">Project Duration</div>
                        </div>
                        <div className="text-center">
                          <Users className="h-5 w-5 text-primary mx-auto mb-1" />
                          <div className="text-sm font-semibold text-foreground">{study.teamSize}</div>
                          <div className="text-xs text-muted-foreground">Team Size</div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;